import { DataProvider } from 'react-admin';
import { UPLOAD_FIELD_NAMES } from '../../../../consts';
import { ApartmentImages } from '../../../../types';

interface UploadedFile {
	driveFile: {
		id: string;
	};
	s3File: {
		key: string;
	};
	originalName?: string;
	name?: string;
}

interface UpdateContext {
	apartmentId: string;
	baseDataProvider: DataProvider;
	existingMediaId: string;
	recordId: string; // The ID of the record in your DB
	type: UPLOAD_FIELD_NAMES;
}

interface UploadResponse {
	files: UploadedFile[];
}

interface DeleteContext {
	recordId: string;
	type: UPLOAD_FIELD_NAMES;
	filePath: string; // e.g., Google Drive file ID if needed
}

export async function deleteFileFromDrive(fileId: string) {
	try {
		const response = await fetch(`/api/media/${fileId}`, {
			method: 'DELETE'
		});

		if (!response.ok) {
			throw new Error(`Failed to delete file from Drive: ${fileId}`);
		}

		console.log(`🗑️ File ${fileId} deleted from Drive.`);
	} catch (error) {
		console.error('Error deleting file from Drive:', error);
	}
}

export async function deleteImage(
	recordId: string,
	fileId: string,
	baseDataProvider: DataProvider
) {
	try {
		await baseDataProvider.delete('images', { id: recordId });
		console.log(`🗑️ Deleted image record ${recordId} from DB.`);
		await deleteFileFromDrive(fileId);
	} catch (error) {
		console.error('Error deleting image:', error);
	}
}

export async function deleteVideo(
	recordId: string,
	fileId: string,
	baseDataProvider: DataProvider
) {
	try {
		await baseDataProvider.delete('videos', { id: recordId });
		console.log(`🗑️ Deleted video record ${recordId} from DB.`);
		await deleteFileFromDrive(fileId);
	} catch (error) {
		console.error('Error deleting video:', error);
	}
}

export async function deleteLayout(
	recordId: string,
	fileId: string,
	baseDataProvider: DataProvider
) {
	try {
		await baseDataProvider.delete('layout', { id: recordId });
		console.log(`🗑️ Deleted layout record ${recordId} from DB.`);
		await deleteFileFromDrive(fileId);
	} catch (error) {
		console.error('Error deleting layout:', error);
	}
}

export async function deleteDocument(
	recordId: string,
	fileId: string,
	baseDataProvider: DataProvider
) {
	try {
		await baseDataProvider.delete('documents', { id: recordId });
		console.log(`🗑️ Deleted document record ${recordId} from DB.`);
		await deleteFileFromDrive(fileId);
	} catch (error) {
		console.error('Error deleting document:', error);
	}
}

export async function handleFileDelete(
	files: DeleteContext[],
	baseDataProvider: DataProvider
): Promise<void> {
	console.log('handleFileDelete > files', files);
	try {
		await Promise.all(
			files.map(
				file => handleSingleFileDelete(file, baseDataProvider) // call the existing single-file version
			)
		);
	} catch (error) {
		console.error(`Failed to delete some files:`, error);
		throw error;
	}
}

export async function handleSingleFileDelete(
	{ recordId, type, filePath }: DeleteContext,
	baseDataProvider: DataProvider
): Promise<void> {
	try {
		switch (type) {
			case UPLOAD_FIELD_NAMES.image:
				await deleteImage(recordId, filePath, baseDataProvider);
				break;
			case UPLOAD_FIELD_NAMES.video:
				await deleteVideo(recordId, filePath, baseDataProvider);
				break;
			case UPLOAD_FIELD_NAMES.layout:
				await deleteLayout(recordId, filePath, baseDataProvider);
				break;
			case UPLOAD_FIELD_NAMES.document:
				await deleteDocument(recordId, filePath, baseDataProvider);
				break;
			default:
				console.warn(`Unknown delete type: ${type}`);
		}
	} catch (error) {
		console.error(`Failed to delete ${type} with ID ${recordId}:`, error);
	}
}

export async function handleFileUpload(
	files: File[],
	apartmentId: string,
	baseDataProvider: DataProvider,
	type: UPLOAD_FIELD_NAMES
): Promise<void> {
	try {
		let uploadResult: any;

		switch (type) {
			case 'image':
				//FIXME: problem here
				uploadResult = await uploadImage(files);

				if (!uploadResult?.success) {
					console.log('File upload failed');
					return;
				}
				console.log(uploadResult);
				// const key = response.results?.[0]?.files?.[0]?.s3File?.key;
				for (const file of uploadResult.results?.[0]?.files) {
					const imageData: ApartmentImages = {
						property_id: apartmentId.replace('apartments-', ''),
						file_path: file.s3File.key,
						title: file.originalName || file.name
					};
					console.log(imageData);

					try {
						await baseDataProvider.create('images', {
							data: imageData
						});
					} catch (error) {
						console.log('Error inserting image record:', error);
					}
				}
				break;

			case 'layout': {
				const results = await uploadLayout(files);
				const uploadedFiles = results?.[0]?.files || [];
				for (const file of uploadedFiles) {
					try {
						await baseDataProvider.create('layout', {
							data: {
								property_id: apartmentId.replace('apartments-', ''),
								file_path: file.s3File.key,
								title: file.originalName || file.name
							}
						});
					} catch (error) {
						console.error('Error inserting layout record:', error);
					}
				}
				break;
			}

			case 'document': {
				const results = await uploadDocuments(files);
				const uploadedFiles = results?.[0]?.files || [];
				for (const file of uploadedFiles) {
					try {
						await baseDataProvider.create('documents', {
							data: {
								property_id: apartmentId.replace('apartments-', ''),
								file_path: file.s3File.key,
								title: file.originalName || file.name
							}
						});
					} catch (error) {
						console.log('Error inserting document record:', error);
					}
				}
				break;
			}

			case 'video': {
				const results = await uploadVideos(files);
				console.log('videoUpload response', results);
				const uploadedFiles = results?.[0]?.files || [];
				for (const file of uploadedFiles) {
					try {
						await baseDataProvider.create('videos', {
							data: {
								property_id: apartmentId.replace('apartments-', ''),
								file_path: file.s3File.key,
								title: file.originalName || file.name
							}
						});
					} catch (error) {
						console.log('Error inserting video record:', error);
					}
				}
				break;
			}

			default:
				console.warn(`Unsupported upload type: ${type}`);
		}
	} catch (error) {
		console.log(`Error during ${type} upload:`, error);
	}
}

export async function handleFileUpdate(
	files: File[],
	{ apartmentId, baseDataProvider, recordId, type }: UpdateContext
): Promise<void> {
	try {
		let uploadResult: any;

		switch (type) {
			case 'image':
				uploadResult = await uploadImage(files);
				break;
			case 'layout':
				uploadResult = await uploadLayout(files);
				break;
			case 'document':
				uploadResult = await uploadDocuments(files);
				break;
			case 'video':
				uploadResult = await uploadVideos(files);
				break;
			default:
				console.warn(`Unsupported update type: ${type}`);
				return;
		}

		const uploadedFile = uploadResult?.results?.[0]?.files?.[0];

		if (!uploadedFile || !uploadedFile.driveFile?.id) {
			console.warn('No file was uploaded, skipping update.');
			return;
		}

		const updatedRecord = {
			property_id: apartmentId.replace('apartments-', ''),
			file_path: uploadedFile.driveFile.id,
			title: uploadedFile.originalName || uploadedFile.name || 'Updated File'
		};

		await baseDataProvider.update(type, {
			id: recordId,
			data: updatedRecord,
			previousData: { id: recordId } // Optional: for react-admin usage
		});

		console.log(`✅ Updated ${type} record ${recordId} with new file.`);
	} catch (error) {
		console.error(`❌ Failed to update ${type} file:`, error);
	}
}

export async function createDirectory(directoryPath: string) {
	return { result: 'properties' };
}

export async function uploadImage(files: File | File[]) {
	try {
		console.log(files);
		if (!files || (Array.isArray(files) && files.length === 0)) {
			throw new Error('No files provided');
		}

		const formData = new FormData();

		const filesArray = Array.isArray(files) ? files : [files];
		// console.log(filesArray);

		filesArray.forEach(file => {
			formData.append(UPLOAD_FIELD_NAMES.image, file); // Append each file under the same 'image' field
		});
		console.log(formData);

		const response = await fetch('/api/upload', {
			method: 'POST',
			body: formData
		});

		if (!response.ok) {
			throw new Error('Failed to upload image(s)');
		}

		const data = await response.json();
		console.log('✅ Image(s) uploaded successfully:', data);
		return data;
	} catch (error) {
		console.log('❌ Error uploading image(s):', error);
	}
}

export async function uploadLayout(
	files: File | File[]
): Promise<UploadResponse[]> {
	try {
		if (!files || (Array.isArray(files) && files.length === 0)) {
			throw new Error('No files provided');
		}

		const formData = new FormData();
		const filesArray = Array.isArray(files) ? files : [files];

		filesArray.forEach(file => {
			formData.append(UPLOAD_FIELD_NAMES.layout, file); // Append each file under the same 'layout' field
		});

		const response = await fetch('/api/upload', {
			method: 'POST',
			body: formData
		});

		if (!response.ok) {
			throw new Error('Failed to upload layout(s)');
		}

		const data = await response.json();
		console.log('✅ Layout(s) uploaded successfully:', data);
		return data.results; // Assuming your response returns an array of URLs
	} catch (error) {
		console.log('❌ Error uploading layout(s):', error);
		return [];
	}
}

export async function uploadDocuments(
	files: File | File[]
): Promise<UploadResponse[]> {
	try {
		if (!files || (Array.isArray(files) && files.length === 0)) {
			throw new Error('No files provided');
		}

		const formData = new FormData();
		const filesArray = Array.isArray(files) ? files : [files];

		filesArray.forEach(file => {
			formData.append(UPLOAD_FIELD_NAMES.document, file); // Append each file under the same 'document' field
		});

		const response = await fetch('/api/upload', {
			method: 'POST',
			body: formData
		});

		if (!response.ok) {
			throw new Error('Failed to upload document(s)');
		}

		const data = await response.json();
		console.log('✅ Document(s) uploaded successfully:', data);
		return data.results; // Assuming your response returns an array of URLs
	} catch (error) {
		console.log('❌ Error uploading document(s):', error);
		return [];
	}
}

export async function uploadVideos(
	files: File | File[]
): Promise<UploadResponse[]> {
	try {
		if (!files || (Array.isArray(files) && files.length === 0)) {
			throw new Error('No files provided');
		}

		const formData = new FormData();
		const filesArray = Array.isArray(files) ? files : [files];

		filesArray.forEach(file => {
			formData.append(UPLOAD_FIELD_NAMES.video, file); // Append each file under the same 'video' field
		});

		const response = await fetch('/api/upload', {
			method: 'POST',
			body: formData
		});

		if (!response.ok) {
			throw new Error('Failed to upload video(s)');
		}

		const data = await response.json();
		console.log('✅ Video(s) uploaded successfully:', data);
		return data.results;
	} catch (error) {
		console.log('❌ Error uploading video(s):', error);
		return [];
	}
}
